/**
 * Rörelse: moderskepp mot målpunkt, units flockar mot sin rallypunkt
 * (skeppet eller temporär rally). Deterministisk: units itereras i arrayordning
 * (stigande id), all matematik i heltal.
 */
import type { SimConfig } from './config';
import { getUnitTier } from './config';
import { clamp, fpFromWorld, fpMoveToward, isqrt } from './fixed';
import type { GameState, Unit } from './state';
import { PLAYER_IDS } from './state';
import { buildSpatialHash } from './spatial';

/** Fixedpoint-steg per tick för en hastighet i world units/s. */
function stepPerTick(speedWorldPerSec: number, ticksPerSec: number): number {
  return Math.max(1, Math.round(fpFromWorld(speedWorldPerSec) / ticksPerSec));
}

export function moveMotherships(state: GameState, config: SimConfig): void {
  const tps = config.match.simTicksPerSecond;
  const step = stepPerTick(config.match.mothership.moveSpeed, tps);
  const maxX = fpFromWorld(config.match.mapSize.width);
  const maxY = fpFromWorld(config.match.mapSize.height);

  for (const id of PLAYER_IDS) {
    const ship = state.motherships[id];
    const tx = clamp(ship.targetX, 0, maxX);
    const ty = clamp(ship.targetY, 0, maxY);
    if (ship.x === tx && ship.y === ty) continue;
    const next = fpMoveToward(ship.x, ship.y, tx, ty, step);
    ship.x = next.x;
    ship.y = next.y;
  }
}

/** Rallypunkt för en unit: spelarens rally point om satt, annars moderskeppet. */
function rallyFor(state: GameState, unit: Unit): { x: number; y: number } {
  const rally = state.players[unit.owner].rallyPoint;
  if (rally) return rally;
  const ship = state.motherships[unit.owner];
  return { x: ship.x, y: ship.y };
}

export function moveUnits(state: GameState, config: SimConfig): void {
  const tps = config.match.simTicksPerSecond;
  const fl = config.match.flocking;
  const sepRadius = fpFromWorld(fl.separationRadius);
  const slack = fpFromWorld(fl.followSlackRadius);
  const sepStep = Math.round(fpFromWorld(fl.separationStrength) / tps);
  const maxX = fpFromWorld(config.match.mapSize.width);
  const maxY = fpFromWorld(config.match.mapSize.height);

  const shipClearance = fpFromWorld(fl.shipClearanceRadius);
  const hash = buildSpatialHash(state.units, sepRadius);
  const neighbors: Unit[] = [];

  // Beräkna alla förflyttningar först, applicera sedan — ingen ordningsberoende
  // återkoppling inom samma tick utöver den fasta id-ordningen.
  const dxs = new Array<number>(state.units.length);
  const dys = new Array<number>(state.units.length);

  for (let i = 0; i < state.units.length; i++) {
    const unit = state.units[i]!;
    const tier = getUnitTier(config.units, unit.family, unit.tier);
    const step = stepPerTick(tier.speed, tps);
    const rally = rallyFor(state, unit);

    // Sök mot rallypunkten, men stå still inom slack-radien (undviker klumpning).
    let dx = 0;
    let dy = 0;
    const toX = rally.x - unit.x;
    const toY = rally.y - unit.y;
    const dist = isqrt(toX * toX + toY * toY);
    if (dist > slack) {
      const move = Math.min(step, dist - slack);
      dx = Math.trunc((toX * move) / dist);
      dy = Math.trunc((toY * move) / dist);
    }

    // Separation: knuffas bort från nära grannar (egna + fiendens, alla är solida).
    hash.queryCircle(unit.x, unit.y, sepRadius, neighbors);
    let sepX = 0;
    let sepY = 0;
    for (const other of neighbors) {
      if (other.id === unit.id) continue;
      const ox = unit.x - other.x;
      const oy = unit.y - other.y;
      const d = isqrt(ox * ox + oy * oy);
      if (d >= sepRadius) continue;
      if (d === 0) {
        // Exakt överlapp: deterministisk knuff baserad på id-paret.
        const angleSeed = (unit.id * 7 + other.id * 13) % 8;
        const dirX = [1, 1, 0, -1, -1, -1, 0, 1][angleSeed]!;
        const dirY = [0, 1, 1, 1, 0, -1, -1, -1][angleSeed]!;
        sepX += dirX * sepStep;
        sepY += dirY * sepStep;
        continue;
      }
      // Styrka avtar linjärt med avståndet: full vid 0, noll vid sepRadius.
      const push = Math.trunc((sepStep * (sepRadius - d)) / sepRadius);
      sepX += Math.trunc((ox * push) / d);
      sepY += Math.trunc((oy * push) / d);
    }

    // Håll fritt runt moderskeppen (units lägger sig i ring, göms inte under skeppet).
    for (const shipOwner of PLAYER_IDS) {
      const ship = state.motherships[shipOwner];
      const ox = unit.x - ship.x;
      const oy = unit.y - ship.y;
      const d = isqrt(ox * ox + oy * oy);
      if (d >= shipClearance) continue;
      if (d === 0) {
        sepX += unit.id % 2 === 0 ? sepStep : -sepStep;
        continue;
      }
      const push = Math.trunc((sepStep * (shipClearance - d)) / shipClearance);
      sepX += Math.trunc((ox * push) / d);
      sepY += Math.trunc((oy * push) / d);
    }

    dxs[i] = dx + sepX;
    dys[i] = dy + sepY;
  }

  for (let i = 0; i < state.units.length; i++) {
    const unit = state.units[i]!;
    unit.x = clamp(unit.x + dxs[i]!, 0, maxX);
    unit.y = clamp(unit.y + dys[i]!, 0, maxY);
  }
}
